'use client';

import { RegistrationTracker } from '@/components/analytics/RegistrationTracker';
import ActionButton from '@/components/form/ActionButton/ActionButton';
import FormText from '@/components/form/FormText/FormText';
import { PRODUCT_NAME } from '@/constants';
import { swal } from '@/lib/sweetalert';
import { yup } from '@/lib/yup';
import layoutStyles from '@/styles/modules/layout.module.scss';
import utilsStyles from '@/styles/modules/utils.module.scss';
import { goMainOneTimeAuth } from '@/utils/functions';
import { yupResolver } from '@hookform/resolvers/yup';
import { pickErrorMessages } from '@monorepo/common';
import { signIn } from 'next-auth/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { postSignin } from '../../api/postSignin';
import styles from './SignInForm.module.scss';

const formScheme = yup.object({
  email: yup.string().label('メールアドレス').required(),
  password: yup.string().label('パスワード').required(),
});

const SignInForm = ({
  callbackUrl = '/',
  showSignup = true,
}: {
  callbackUrl: string;
  showSignup?: boolean;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showRegistrationTracking, setShowRegistrationTracking] = useState(false);
  const { handleSubmit, getValues, control } = useForm({
    mode: 'onChange',
    defaultValues: {
      email: '',
      password: '',
    },
    resolver: yupResolver(formScheme),
  });

  const submit = async () => {
    try {
      setIsLoading(true);
      let isGoingMain = false;
      // ログインしてトークン取得
      const { result } = await postSignin({
        email: getValues('email'),
        password: getValues('password'),
      }).catch(async (error) => {
        if (error.code === 204) {
          // 未joinの場合
          await swal.alert({
            text: `次の画面で${PRODUCT_NAME}に登録してください`,
          });
          isGoingMain = true;
          goMainOneTimeAuth({
            redirectPath: `/${process.env.NEXT_PUBLIC_SERVICE_ID}/auth/join`,
            token: error.result.token,
          });
        } else {
          throw error;
        }
        return error;
      });

      //   トークンでログイン
      if (!isGoingMain) {
        await signIn('with-token', {
          token: result.token,
          // ログイン成功時の遷移先
          callbackUrl: callbackUrl,
        });
      }
    } catch (error) {
      await swal.messages({ messages: pickErrorMessages(error) });
      setIsLoading(false);
    }
  };

  const submitError = () => {
    swal.invalid();
  };

  const handleSignupClick = () => {
    // 会員登録開始トラッキング
    RegistrationTracker.trackRegistrationStart();

    // 外部サイトへの遷移トラッキング
    RegistrationTracker.trackExternalRedirect(callbackUrl);

    // 離脱検知を開始
    setShowRegistrationTracking(true);

    // 外部サイトに遷移
    location.href = `${process.env.NEXT_PUBLIC_MAIN_URL}/${process.env.NEXT_PUBLIC_SERVICE_ID}/auth/signup?callbackUrl=${callbackUrl}`;
  };

  return (
    <>
      {/* 会員登録フロー離脱検知 */}
      {showRegistrationTracking}
      <form onSubmit={handleSubmit(submit, submitError)}>
        <div className={layoutStyles.formWrapper}>
          <FormText type='email' label='メールアドレス' name='email' control={control} />
          <FormText type='password' label='パスワード' name='password' control={control} />
        </div>
        <div className={`${utilsStyles.textRight} ${utilsStyles.mtXSmall}`}>
          <a href={`${process.env.NEXT_PUBLIC_MAIN_URL}/forgot-password`} className={utilsStyles.textXSmall}>
            パスワードをお忘れの方
          </a>
        </div>
        <div className={`${utilsStyles.centeredVertical} ${utilsStyles.mtMedium}`}>
          <ActionButton type='submit' disabled={isLoading} isLoading={isLoading} dataTestId='login-button'>
            ログイン
          </ActionButton>
        </div>
        {/* 新規会員登録導線 */}
        {showSignup && (
          <>
            <hr className={`${styles.hr} ${utilsStyles.mtMedium}`} />
            <div className={`${utilsStyles.centeredVertical} ${utilsStyles.mtMedium}`}>
              <p className={`${utilsStyles.textLarge} ${utilsStyles.textBold}`}>アカウントをお持ちでない方</p>
              <p className={`${utilsStyles.mtXSmall} ${utilsStyles.textSmall}`}>
                {PRODUCT_NAME}のご利用には会員登録(Butterfly ID)が必要になります。
              </p>
            </div>
            <div className={`${layoutStyles.centeredBtnWrapper} ${utilsStyles.mtSmall}`}>
              <ActionButton onClick={handleSignupClick} color='primary'>
                新規会員登録
              </ActionButton>
            </div>
          </>
        )}
      </form>
    </>
  );
};
export default SignInForm;
